import { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function HeroSection() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [service, setService] = useState('');
  const [postcode, setPostcode] = useState('');
  const [error, setError] = useState('');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!service) { setError('Please select a service'); return; }
    if (!postcode.trim()) { setError('Please enter your postcode'); return; }
    setError('');
    setSubmitting(true);

    // Submit to form endpoint in background
    try {
      const body = new URLSearchParams({ service, postcode });
      fetch('https://readdy.ai/api/form/d7426jr41deorl02el20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch {
      // Silent fail - we still redirect to quote wizard
    }

    // Redirect to solar quote page with pre-filled data
    const params = new URLSearchParams({ service, postcode });
    navigate(`/solar-quote?${params.toString()}`);
  };

  const QuoteCard = () => (
    <div className="bg-white rounded-2xl p-6 md:p-7">
      {submitted ? (
        <div className="text-center py-4">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 animate-scale-in">
            <i className="ri-checkbox-circle-fill text-[#7AC943] text-5xl" />
          </div>
          <h3 className="font-heading font-bold text-lg text-[#1F2933] mb-1">Quote Request Sent!</h3>
          <p className="text-gray-500 text-sm">Redirecting to complete your quote...</p>
        </div>
      ) : (
        <>
          <div className="text-[#4CAF50] text-xs font-bold uppercase tracking-widest mb-1">FREE QUOTE · NO OBLIGATION</div>
          <h2 className="font-heading font-bold text-[#1F2933] text-xl md:text-2xl leading-tight mb-2">
            Get a fixed price on renewables today
          </h2>
          <p className="text-gray-500 text-sm mb-4 leading-relaxed">
            Solar panels, battery storage, EV chargers &amp; maintenance — fixed prices, finance available.
          </p>

          <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <div className="relative">
              <select
                name="service"
                value={service}
                onChange={(e) => setService(e.target.value)}
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm text-[#1F2933] appearance-none focus:outline-none focus:border-[#4CAF50] transition-colors cursor-pointer bg-white"
              >
                <option value="">What do you need?</option>
                <option value="Solar Installation">Solar Installation</option>
                <option value="Battery Storage">Battery Storage</option>
                <option value="Solar + Battery Bundle">Solar + Battery Bundle</option>
                <option value="EV Charger">EV Charger</option>
                <option value="Repair / Maintenance">Repair / Maintenance</option>
              </select>
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-down-s-line text-gray-400 text-lg" />
              </div>
            </div>

            <input
              type="text"
              name="postcode"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value.toUpperCase())}
              placeholder="Enter your postcode"
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm text-[#1F2933] focus:outline-none focus:border-[#4CAF50] transition-colors"
              maxLength={8}
            />

            {error && <p className="text-red-500 text-xs">{error}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="w-full shimmer-btn text-white font-bold py-3.5 rounded-xl text-sm hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-70"
            >
              {submitting ? 'Getting your quote...' : 'Get your fixed price →'}
            </button>
          </form>

          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('openBooking'))}
            className="w-full mt-2 flex items-center justify-center gap-2 bg-[#f59e0b] text-white font-bold py-3 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap hover:bg-[#d97706]"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-check-line text-sm" />
            </div>
            Book an appointment
            <span className="bg-white/20 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">GHL</span>
          </button>

          <ul className="flex flex-col gap-2 mt-4">
            {[
              'Quote back within the next working day',
              'Fixed pricing — finance options available',
              'MCS &amp; OZEV accredited engineers',
            ].map((item) => (
              <li key={item} className="flex items-start gap-2 text-xs text-gray-600">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-[#4CAF50] text-sm font-bold" />
                </div>
                <span dangerouslySetInnerHTML={{ __html: item }} />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );

  return (
    <section
      id="quote-section"
      className="bg-[#145A32] relative overflow-hidden pt-24 md:pt-40"
    >
      {/* Decorative background circles */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/2 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-white/5 translate-y-1/2 -translate-x-1/2 pointer-events-none" />
      <div className="absolute top-1/3 left-1/4 w-4 h-4 rounded-full bg-[#7AC943]/40 animate-pulse pointer-events-none" />
      <div className="absolute top-1/4 right-1/3 w-2.5 h-2.5 rounded-full bg-[#7AC943]/30 animate-pulse pointer-events-none" style={{ animationDelay: '1s' }} />
      <div className="absolute bottom-1/3 right-1/4 w-3 h-3 rounded-full bg-white/20 animate-pulse pointer-events-none" style={{ animationDelay: '0.5s' }} />

      {/* ── MOBILE LAYOUT ── */}
      <div className="md:hidden px-4 pb-8">

        {/* Headline */}
        <div className={`transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Service pill row */}
          <div className="flex flex-wrap justify-center gap-2 pt-6 mb-4">
            {[
              { icon: 'ri-sun-line', label: 'Solar' },
              { icon: 'ri-battery-2-charge-line', label: 'Battery' },
              { icon: 'ri-tools-line', label: 'Maintenance' },
              { icon: 'ri-charging-pile-line', label: 'EV Charging' },
            ].map((s) => (
              <span key={s.label} className="flex items-center gap-1 bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                <i className={`${s.icon} text-[#7AC943] text-xs`} />
                {s.label}
              </span>
            ))}
          </div>
          <h1 className="font-heading font-black text-white text-3xl text-center leading-tight mb-2">
            Solar, Battery &amp;<br />Renewables Made Easy
          </h1>
          <p className="text-white/70 text-center text-sm mb-2 leading-relaxed">
            Fixed pricing on solar panels, battery storage, EV chargers &amp; maintenance. MCS &amp; OZEV certified engineers.
          </p>
          <div className="flex items-center justify-center gap-1.5 mb-4">
            <div className="w-3 h-3 flex items-center justify-center"><i className="ri-map-pin-2-line text-[#7AC943] text-xs" /></div>
            <span className="text-[#7AC943] text-xs font-semibold">London · Essex · Suffolk · Cambridge</span>
          </div>

          {/* Google reviews badge — mobile */}
          <a
            href="https://share.google/fbMmoctdetcjPCoMq"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="flex items-center justify-center gap-2.5 bg-white/10 border border-white/20 rounded-2xl px-4 py-2.5 mb-5 cursor-pointer hover:bg-white/15 transition-all"
          >
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 48 48" className="w-5 h-5">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                <path fill="none" d="M0 0h48v48H0z"/>
              </svg>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-[#FFB800] text-xs" />)}
              </div>
              <span className="text-white font-black text-sm">5.0</span>
              <span className="text-white/60 text-xs">·</span>
              <span className="text-white/80 text-xs font-medium">449 Google reviews</span>
            </div>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-external-link-line text-white/40 text-xs" />
            </div>
          </a>
        </div>

        {/* Hero image */}
        <div className={`relative w-full h-52 mb-4 rounded-2xl overflow-hidden bg-[#0d4025] transition-all duration-700 delay-200 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <img
            src="https://public.readdy.ai/ai/img_res/d382aac2d6ca791cf47c60b61bdc0a59.jpg"
            alt="Solar panels and battery storage on UK home"
            className="w-full h-full object-cover object-center"
            loading="eager"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          {/* Subtle dark overlay on mobile image too */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a200f]/50 via-transparent to-transparent pointer-events-none" />
          {/* Savings badge */}
          <div className="absolute top-3 right-3 w-20 h-20 rounded-full bg-[#7AC943] flex flex-col items-center justify-center text-white text-center leading-tight animate-float">
            <span className="text-[9px] font-bold uppercase tracking-tight">Cut bills</span>
            <span className="text-xl font-black leading-none">70%</span>
            <span className="text-[9px] font-bold uppercase tracking-tight">with solar</span>
          </div>
        </div>

        {/* Quote card */}
        <div className={`transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <QuoteCard />
        </div>
      </div>

      {/* ── DESKTOP LAYOUT ── */}
      <div className="hidden md:block">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex flex-row items-end min-h-[520px]">

            {/* Left */}
            <div className="relative flex-1 flex items-end justify-start min-h-[520px]">

              {/* Savings badge — floating */}
              <div className={`absolute top-8 left-6 w-32 h-32 rounded-full bg-[#7AC943] flex flex-col items-center justify-center text-white text-center z-10 leading-tight transition-all duration-700 delay-200 animate-float ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}>
                <span className="text-[10px] font-bold uppercase tracking-tight">Cut bills</span>
                <span className="text-3xl font-black leading-none">70%</span>
                <span className="text-[10px] font-bold uppercase tracking-tight">with solar</span>
              </div>

              {/* Decorative ring */}
              <div className={`absolute top-2 left-2 w-44 h-44 rounded-full border-2 border-[#7AC943]/30 animate-spin-slow transition-all duration-700 delay-300 ${visible ? 'opacity-100' : 'opacity-0'}`} />

              {/* Headline block */}
              <div className={`absolute top-10 left-52 z-10 max-w-lg transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
                {/* Service pills */}
                <div className={`flex flex-wrap gap-2 mb-4 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  {[
                    { icon: 'ri-sun-line', label: 'Solar Panels' },
                    { icon: 'ri-battery-2-charge-line', label: 'Battery Storage' },
                    { icon: 'ri-tools-line', label: 'Maintenance' },
                    { icon: 'ri-charging-pile-line', label: 'EV Charging' },
                  ].map((s) => (
                    <span key={s.label} className="flex items-center gap-1.5 bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                      <i className={`${s.icon} text-[#7AC943] text-xs`} />
                      {s.label}
                    </span>
                  ))}
                </div>

                <h1 className="font-heading font-black text-white text-4xl xl:text-5xl leading-tight mb-3">
                  Solar, Battery &amp;<br />Renewables Made Easy
                </h1>
                <p className="text-white/70 text-base leading-relaxed mb-2">
                  Fixed pricing on solar panels, battery storage, EV chargers &amp; full maintenance. MCS &amp; OZEV accredited engineers across the UK.
                </p>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-2-line text-[#7AC943] text-sm" /></div>
                  <span className="text-[#7AC943] text-sm font-semibold">London · Essex · Suffolk · Cambridge</span>
                </div>

                {/* Google reviews badge — desktop */}
                <a
                  href="https://share.google/fbMmoctdetcjPCoMq"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-white/10 border border-white/20 rounded-2xl px-4 py-2.5 mb-5 cursor-pointer hover:bg-white/15 transition-all"
                >
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 48 48" className="w-5 h-5">
                      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                      <path fill="none" d="M0 0h48v48H0z"/>
                    </svg>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-[#FFB800] text-sm" />)}
                    </div>
                    <span className="text-white font-black text-base">5.0</span>
                    <span className="text-white/50 text-sm">·</span>
                    <span className="text-white/80 text-sm font-medium">449 Google reviews</span>
                  </div>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-external-link-line text-white/40 text-xs" />
                  </div>
                </a>

                {/* Quick stat pills */}
                <div className={`flex flex-wrap gap-3 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  {[
                    { icon: 'ri-sun-line', label: '500+ Solar Installs' },
                    { icon: 'ri-award-line', label: 'MCS Certified' },
                    { icon: 'ri-money-pound-circle-line', label: 'Finance Available' },
                  ].map((stat) => (
                    <div key={stat.label} className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1.5 text-white text-xs font-semibold">
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className={`${stat.icon} text-[#7AC943] text-xs`} />
                      </div>
                      {stat.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Hero image */}
              <img
                src="https://readdy.ai/api/search-image?query=modern%20detached%20British%20house%20with%20full%20roof%20monocrystalline%20solar%20panel%20array%20and%20home%20battery%20storage%20unit%20on%20wall%2C%20bright%20sunny%20blue%20sky%2C%20UK%20suburban%20garden%20setting%2C%20photorealistic%20architecture%20photography%2C%20professional%20real%20estate%20image%2C%20no%20text%2C%20clean%20background%2C%20green%20energy&width=800&height=560&seq=hero-desktop-allrenew-2025&orientation=landscape"
                alt="Solar panels and battery storage on UK home"
                loading="eager"
                className={`absolute bottom-0 left-0 w-full md:w-[700px] h-[460px] object-contain object-bottom transition-all duration-1000 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              />
              {/* Dark overlay — improves text readability over the bright image */}
              <div
                className="absolute bottom-0 left-0 w-full md:w-[700px] h-[460px] pointer-events-none z-[5]"
                style={{ background: 'linear-gradient(135deg, rgba(8,32,15,0.88) 0%, rgba(8,32,15,0.70) 35%, rgba(8,32,15,0.35) 65%, rgba(8,32,15,0.05) 100%)' }}
              />
            </div>

            {/* Right: Quote card */}
            <div className={`w-[390px] flex-shrink-0 pb-10 pl-6 relative z-10 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <QuoteCard />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
